import * as React from 'react';
import {
  Box, Typography, Card, CardMedia, CardContent,
  Dialog, IconButton, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SharePointService from '../services/sharepoint.service';

// Hilfsfunktion: YouTube-Link -> Embed-URL
function toEmbedUrl(youtubeUrl, { autoplay = true } = {}) {
  try {
    const u = new URL(youtubeUrl);
    const videoId = u.searchParams.get('v');
    return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&rel=0&modestbranding=1&controls=1`;
  } catch {
    return youtubeUrl;
  }
}

// Hilfsfunktion: Thumbnail aus YouTube-Link generieren
function getYoutubeThumbnail(url) {
  try {
    const u = new URL(url);
    const videoId = u.searchParams.get('v');
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  } catch {
    return "https://placehold.co/600x338?text=Video";
  }
}

export default function ProjectVideos() {
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const [activeUrl, setActiveUrl] = React.useState(null);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  React.useEffect(() => {
    const fetchVideos = async () => {
      try {
        const result = await SharePointService.getProjectVideos(); 
        setProjects(result || []);
      } catch (err) {
        console.error("Fehler beim Laden der Videos:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  const openVideo = (url) => {
    setActiveUrl(url);
    setOpen(true);
  };

  const closeVideo = () => {
    setOpen(false);
    setActiveUrl(null);
  };

  const projectsWithVideos = projects
    .filter(p => p.Videos && p.Videos.length > 0)
    .map(p => ({
      ...p,
      Videos: [...p.Videos].sort((a, b) => {
        if (!a.Uploaded) return 1;
        if (!b.Uploaded) return -1;
        // Hochgeladen ist ein String wie "12.11.2025"
        const [da, ma, ya] = a.Uploaded.split('.').map(Number);
        const [db, mb, yb] = b.Uploaded.split('.').map(Number);
        const dateA = new Date(ya, ma - 1, da);
        const dateB = new Date(yb, mb - 1, db);
        return dateB - dateA; // 👈 absteigend
      })
    }));

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : projectsWithVideos.length === 0 ? (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ textAlign: "center", py: 4 }}
        >
          Keine Baufortschrittvideos für Ihre Projekte gefunden.
        </Typography>
      ) : (
        projectsWithVideos.map((proj) => (
          <Box key={proj.Title}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              {proj.ProjektTitle}
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                overflowX: "auto",
                pb: 1,
                "&::-webkit-scrollbar": { height: 8 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#ccc",
                  borderRadius: 4
                }
              }}
            >
              {proj.Videos.map((video, idx) => (
                <Card
                  key={idx}
                  sx={{
                    minWidth: 240,
                    maxWidth: 240,
                    flex: "0 0 auto",
                    cursor: "pointer",
                    "&:hover": { boxShadow: 3 }
                  }}
                  onClick={() => openVideo(video.Link)}
                >
                  <CardMedia
                    component="img"
                    height="135"
                    image={getYoutubeThumbnail(video.Link)}
                    alt={video.Title}
                  />
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden"
                      }}
                    >
                      {video.Title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {video.Uploaded}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        ))
      )}

      <Dialog
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
        open={open}
        onClose={closeVideo}
        PaperProps={{ sx: { backgroundColor: "black" } }}
      >
        <Box sx={{ p: { xs: 0, sm: 2 }, height: fullScreen ? "100vh" : "auto" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              bgcolor: "white",
              px: 1
            }}
          >
            <IconButton onClick={closeVideo} aria-label="Schließen">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              ...(fullScreen ? { height: "100vh" } : { aspectRatio: "16 / 9" }),
              backgroundColor: "black"
            }}
          >
            {activeUrl && (
              <iframe
                title="Video"
                src={toEmbedUrl(activeUrl, { autoplay: true })}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  border: 0
                }}
              />
            )}
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
